import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faGift,
    faPlus,
    faEdit,
    faPaperPlane,
    faCheckCircle,
    faInfoCircle,
    faArrowRight,
    faArrowLeft,
    faTag,
    faCoins
} from '@fortawesome/free-solid-svg-icons';
import DocumentationLayout from '../../../components/documentation/DocumentationLayout';

function MarketingRecompensas() {
    return (
        <DocumentationLayout>
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                        <FontAwesomeIcon icon={faGift} className="text-purple-600 text-2xl" />
                    </div>
                    <h1 className="text-4xl font-bold text-gray-800 mb-4">
                        Gestión de Recompensas
                    </h1>
                    <p className="text-xl text-gray-600">
                        Aprende a crear y gestionar recompensas (premios y descuentos) para tus clientes, 
                        enviarlas y gestionar su canje.
                    </p>
                </div>

                {/* Introducción */}
                <div className="bg-white rounded-lg shadow-md p-8 mb-8">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">
                        ¿Qué son las Recompensas?
                    </h2>
                    <p className="text-gray-600 mb-4">
                        Las recompensas son premios o descuentos que puedes otorgar a tus clientes como parte 
                        de tu programa de fidelización. Existen dos tipos principales:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="border rounded-lg p-4 bg-blue-50">
                            <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                <FontAwesomeIcon icon={faGift} className="text-blue-600 mr-2" />
                                Premios
                            </h3>
                            <p className="text-sm text-gray-600">
                                Premios físicos o digitales que los clientes pueden canjear con puntos. 
                                Ejemplos: productos gratis, servicios especiales, regalos.
                            </p>
                        </div>
                        <div className="border rounded-lg p-4 bg-green-50">
                            <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                                <FontAwesomeIcon icon={faTag} className="text-green-600 mr-2" />
                                Descuentos
                            </h3>
                            <p className="text-sm text-gray-600">
                                Descuentos monetarios o porcentuales que se pueden aplicar a las compras. 
                                Pueden tener un monto mínimo de compra.
                            </p>
                        </div>
                    </div>
                </div>

                {/* Crear Recompensas */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faPlus} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Crear una Recompensa
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 1: Acceder a Gestión de Recompensas
                        </h3>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Desde el menú principal, accede a <strong>"Marketing" → "Recompensas"</strong> o <strong>"Gestión de Recompensas"</strong></span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Verás la lista de todas tus recompensas existentes (si las tienes)</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Haz clic en el botón <strong>"Crear Recompensa"</strong></span>
                            </li>
                        </ol>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 2: Información Básica
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Completa los campos básicos de la recompensa:
                        </p>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Nombre:</strong> Un nombre descriptivo para la recompensa (ej: "Descuento Verano 20%")</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Descripción:</strong> Explica claramente qué incluye la recompensa</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Marca:</strong> Selecciona la marca a la que pertenece la recompensa</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Tipo:</strong> Elige entre "Premio" o "Descuento"</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 3: Configurar Premio
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Si seleccionaste "Premio", configura:
                        </p>
                        <ul className="space-y-2 text-gray-600">
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCoins} className="text-yellow-500 mr-2 mt-1" />
                                <span><strong>Puntos requeridos:</strong> Número de puntos que el cliente necesita para canjear el premio</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Fecha de caducidad (opcional):</strong> Fecha límite para canjear el premio</span>
                            </li>
                            <li className="flex items-start">
                                <FontAwesomeIcon icon={faCheckCircle} className="text-green-500 mr-2 mt-1" />
                                <span><strong>Estado:</strong> Activo o Inactivo (solo los premios activos pueden ser canjeados)</span>
                            </li>
                        </ul>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 4: Configurar Descuento
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Si seleccionaste "Descuento", puedes configurar:
                        </p>
                        <div className="space-y-4">
                            <div className="border-l-4 border-blue-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Tipo de Descuento</h4>
                                <p className="text-sm text-gray-600 mb-2">
                                    Elige <strong>uno</strong> de los siguientes:
                                </p>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• <strong>Cantidad Monetaria:</strong> Descuento fijo (ej: 10€, $5)</li>
                                    <li>• <strong>Porcentaje:</strong> Descuento porcentual (ej: 20%)</li>
                                </ul>
                                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-3">
                                    <p className="text-xs text-yellow-800">
                                        <strong>⚠️ Importante:</strong> Solo puedes usar cantidad monetaria O porcentaje, no ambos a la vez.
                                    </p>
                                </div>
                            </div>
                            <div className="border-l-4 border-green-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Monto Mínimo (Opcional)</h4>
                                <p className="text-sm text-gray-600">
                                    Establece un monto mínimo de compra para que el descuento sea aplicable. 
                                    Por ejemplo, si estableces 50€, el descuento solo se aplicará en compras de 50€ o más.
                                </p>
                            </div>
                            <div className="border-l-4 border-purple-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Moneda</h4>
                                <p className="text-sm text-gray-600 mb-2">
                                    Selecciona la moneda para el descuento:
                                </p>
                                <ul className="text-sm text-gray-600 space-y-1">
                                    <li>• Dólares (USD)</li>
                                    <li>• Euros (EUR)</li>
                                    <li>• Soles (PEN)</li>
                                    <li>• Pesos Chilenos (CLP)</li>
                                </ul>
                            </div>
                            <div className="border-l-4 border-orange-500 pl-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Fecha de Caducidad (Opcional)</h4>
                                <p className="text-sm text-gray-600">
                                    Establece una fecha límite para usar el descuento. Después de esta fecha, 
                                    el descuento ya no será válido.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Paso 5: Guardar la Recompensa
                        </h3>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Revisa toda la información ingresada</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Haz clic en <strong>"Guardar"</strong> o <strong>"Crear Recompensa"</strong></span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>La recompensa aparecerá en tu lista de recompensas</span>
                            </li>
                        </ol>
                    </div>
                </section>

                {/* Ver y Editar Recompensas */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faEdit} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Ver y Editar Recompensas
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Lista de Recompensas
                        </h3>
                        <p className="text-gray-600 mb-4">
                            En la página principal de recompensas verás dos secciones:
                        </p>
                        <div className="space-y-4">
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Premios</h4>
                                <p className="text-sm text-gray-600">
                                    Todos los premios que has creado, con información sobre puntos requeridos y marca.
                                </p>
                            </div>
                            <div className="border rounded-lg p-4">
                                <h4 className="font-semibold text-gray-800 mb-2">Descuentos</h4>
                                <p className="text-sm text-gray-600">
                                    Todos los descuentos que has creado, con información sobre tipo de descuento, 
                                    monto mínimo y moneda.
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Editar una Recompensa
                        </h3>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Encuentra la recompensa que quieres editar en la lista</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Haz clic en el botón <strong>"Editar"</strong></span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Se abrirá el mismo formulario de creación, pero con los datos actuales</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                                <span>Modifica los campos que necesites y haz clic en <strong>"Guardar"</strong></span>
                            </li>
                        </ol>
                    </div>
                </section>

                {/* Enviar Premios */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faPaperPlane} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Enviar Premios a Clientes
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Cómo Enviar un Premio
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Puedes enviar premios directamente a clientes desde la página de gestión de clientes:
                        </p>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Ve a la página de <strong>"Gestión de Clientes"</strong></span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Encuentra el cliente al que quieres enviar el premio</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Haz clic en el botón <strong>"Enviar Premio"</strong></span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                                <span>Selecciona el premio que quieres enviar de la lista desplegable</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">5</span>
                                <span>Haz clic en <strong>"Enviar"</strong> para confirmar</span>
                            </li>
                        </ol>
                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mt-4">
                            <p className="text-sm text-blue-800">
                                <strong>💡 Nota:</strong> Si el cliente ya tiene un premio sin canjear del mismo tipo, 
                                el sistema lo invalidará automáticamente antes de enviar el nuevo premio.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Canjear Premios */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Canjear Premios
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8 mb-6">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Cómo Canjear un Premio
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Cuando un cliente tiene un premio disponible, puedes canjearlo desde la página de gestión de clientes:
                        </p>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Ve a la página de <strong>"Gestión de Clientes"</strong></span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Encuentra el cliente que tiene el premio</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Haz clic en el botón <strong>"Canjear Premio"</strong></span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                                <span>Se mostrará una lista de premios disponibles para ese cliente</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">5</span>
                                <span>Selecciona el premio que quieres canjear</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">6</span>
                                <span>Haz clic en <strong>"Canjear"</strong> para confirmar</span>
                            </li>
                        </ol>
                        <div className="bg-green-50 border-l-4 border-green-500 p-4 mt-4">
                            <p className="text-sm text-green-800">
                                <strong>✅ Información del Premio:</strong> Cada premio tiene un código único que se muestra 
                                cuando se canjea. Este código puede ser usado para verificar el canje.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Aplicar Descuentos */}
                <section className="mb-12">
                    <div className="flex items-center mb-6">
                        <div className="bg-purple-100 p-3 rounded-lg mr-4">
                            <FontAwesomeIcon icon={faTag} className="text-purple-600 text-xl" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-800">
                            Aplicar Descuentos a Tickets
                        </h2>
                    </div>

                    <div className="bg-white rounded-lg shadow-md p-8">
                        <h3 className="text-xl font-semibold text-gray-800 mb-4">
                            Cómo Aplicar Descuentos
                        </h3>
                        <p className="text-gray-600 mb-4">
                            Los descuentos se pueden aplicar directamente a los tickets de compra:
                        </p>
                        <ol className="space-y-3 text-gray-600">
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">1</span>
                                <span>Desde la vista de un ticket o durante el proceso de venta</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">2</span>
                                <span>Haz clic en <strong>"Aplicar Descuentos"</strong></span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">3</span>
                                <span>Se mostrará una lista de descuentos disponibles para ese cliente</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">4</span>
                                <span>Selecciona uno o más descuentos que quieres aplicar</span>
                            </li>
                            <li className="flex items-start">
                                <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-3 mt-0.5">5</span>
                                <span>Haz clic en <strong>"Aplicar Descuentos"</strong> para confirmar</span>
                            </li>
                        </ol>
                        <div className="bg-purple-50 border-l-4 border-purple-500 p-4 mt-4">
                            <p className="text-sm text-purple-800">
                                <strong>💡 Nota:</strong> Los descuentos se aplicarán automáticamente si cumplen las condiciones 
                                (monto mínimo, fecha de caducidad, etc.). Puedes aplicar múltiples descuentos a un mismo ticket.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Consejos Finales */}
                <div className="bg-purple-50 rounded-lg p-8 mb-8">
                    <h3 className="text-lg font-semibold text-purple-800 mb-4">
                        💡 Consejos para Recompensas Exitosas
                    </h3>
                    <ul className="space-y-3 text-gray-700">
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Equilibra puntos y premios:</strong> Asegúrate de que los puntos requeridos sean 
                            razonables y que los premios sean atractivos para tus clientes.</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Descuentos atractivos pero sostenibles:</strong> Ofrece descuentos que incentiven 
                            compras sin comprometer tu margen de beneficio.</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Usa fechas de caducidad:</strong> Establece fechas límite para crear urgencia 
                            y motivar a los clientes a usar sus recompensas.</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Mantén las recompensas activas:</strong> Revisa regularmente qué recompensas están 
                            activas y desactiva las que ya no quieres ofrecer.</span>
                        </li>
                        <li className="flex items-start">
                            <FontAwesomeIcon icon={faCheckCircle} className="text-purple-600 mr-2 mt-1" />
                            <span><strong>Comunica las recompensas:</strong> Informa a tus clientes sobre las recompensas 
                            disponibles para aumentar su uso.</span>
                        </li>
                    </ul>
                </div>

                {/* Navegación */}
                <div className="flex justify-between items-center pt-6 border-t">
                    <Link
                        to="/documentation/marketing"
                        className="text-gray-600 hover:text-gray-800 font-medium inline-flex items-center"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-2" />
                        Volver a Marketing
                    </Link>
                    <Link
                        to="/documentation/marketing/email-marketing"
                        className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 transition-colors inline-flex items-center"
                    >
                        Siguiente: Email Marketing
                        <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
                    </Link>
                </div>
            </div>
        </DocumentationLayout>
    );
}

export default MarketingRecompensas;

